import { PokeButton } from "@app/components/poke/shadcn/ui/button";
import {
  PokeCommand,
  PokeCommandEmpty,
  PokeCommandGroup,
  PokeCommandInput,
  PokeCommandItem,
  PokeCommandList,
} from "@app/components/poke/shadcn/ui/command";
import { PokeFormControl } from "@app/components/poke/shadcn/ui/form";
import type { AsyncEnumValues, EnumValues } from "@app/types/poke/plugins";
import {
  ChevronDown,
  cn,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@dust-tt/sparkle";
import { CheckCircle, Circle } from "lucide-react";
import React from "react";

interface EnumSelectProps {
  label?: string;
  onValuesChange: (values: string[]) => void;
  options: AsyncEnumValues | EnumValues;
  placeholder?: string;
  values?: string[];
  multiple: boolean;
}

export function EnumSelect({
  label,
  onValuesChange,
  options,
  placeholder = "Select value",
  values,
  multiple,
}: EnumSelectProps) {
  const [open, setOpen] = React.useState(false);

  const optionLabelByValue = React.useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options]
  );

  let title = values?.length
    ? values
        .map((value) => optionLabelByValue.get(value) ?? value)
        .sort()
        .join(", ")
    : placeholder;

  if (title.length > 80) {
    title = `${values?.length} items selected`;
  }

  return (
    <PopoverRoot modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PokeFormControl>
          <PokeButton
            variant="outline"
            role="combobox"
            className={cn(
              "w-auto justify-between border-border-dark bg-background " + "",
              !values?.length && "text-muted-foreground"
            )}
          >
            {title}
            <ChevronDown className="opacity-50" />
          </PokeButton>
        </PokeFormControl>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[var(--radix-popover-trigger-width)]"
        mountPortal={false}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        onWheelCapture={(e) => {
          e.stopPropagation();
        }}
        onTouchMoveCapture={(e) => {
          e.stopPropagation();
        }}
      >
        <PokeCommand className="gap-2 py-3">
          <PokeCommandInput
            placeholder={label}
            className="h-9 p-2"
            onKeyDown={(e) => e.stopPropagation()}
          />
          <PokeCommandList>
            <PokeCommandEmpty>No values found.</PokeCommandEmpty>
            <PokeCommandGroup>
              {options.map((option) => {
                const isSelected = values?.includes(option.value);
                return (
                  <PokeCommandItem
                    value={option.label}
                    keywords={[option.value.replaceAll("_", " ")]}
                    key={option.value}
                    onSelect={() => {
                      onValuesChange([option.value]);
                      if (!multiple) {
                        setOpen(false);
                      } else {
                        if (isSelected) {
                          onValuesChange(
                            values?.filter((value) => value !== option.value) ??
                              []
                          );
                        } else {
                          onValuesChange([...(values ?? []), option.value]);
                        }
                      }
                    }}
                  >
                    <div className="flex w-full items-center gap-2">
                      {multiple ? (
                        isSelected ? (
                          <CheckCircle className="h-4 w-4 text-success-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-primary-400" />
                        )
                      ) : null}
                      <span
                        className={cn(
                          option.checked && "font-medium",
                          "text-primary-900"
                        )}
                      >
                        {option.label}
                      </span>
                    </div>
                  </PokeCommandItem>
                );
              })}
            </PokeCommandGroup>
          </PokeCommandList>
        </PokeCommand>
      </PopoverContent>
    </PopoverRoot>
  );
}
